import {Component, DestroyRef, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {debounceTime, Subject} from 'rxjs';
import {AntibiotikaDTO, LarmKriteriumDTO} from '../../../shared/models/models';
import {StandardNamn} from '../../../shared/models/standard-namn';
import {StandardNamnService} from '../../standard-namn/standard-namn.service';
import {LarmTyp} from '../larm-kriterium.component';
import {LarmKriteriumService} from '../larm-kriterium.service';
import {isInteger} from 'lodash';
import {MIC_VALUES} from 'src/app/shared/models/constants';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {  SMEs } from 'src/app/shared/models/constants';

@Component({
    selector: 'lib-nytt-larm-kriterium',
    templateUrl: './nytt-larm-kriterium.component.html',
    styleUrls: ['./nytt-larm-kriterium.component.scss'],
    standalone: false
})
export class NyttLarmKriteriumComponent implements OnInit {
  protected zoneSubject$ = new Subject<{value: number, kriteria: AntibiotikaDTO}>();

  protected smittamne: StandardNamn[] = [];
  protected provmaterial: StandardNamn[] = [];
  protected analyser: StandardNamn[] = [];
  protected antibiotika: StandardNamn[] = [];
  protected selectedAnalyser: StandardNamn[] = [];
  protected selectedProvmaterial: StandardNamn[] = [];
  protected larmTyper: LarmTyp[] = [LarmTyp.NATIONELL_PUBLIK, LarmTyp.NATIONELL_INTERN];
  protected readonly LarmTyp = LarmTyp;
  //protected selectedLab?: string;
  protected defaultSmittamne = { standardNamn: '-- Välj smittämne --' };
  protected defaultAntibiotika = { standardNamn: '-- Välj antibiotika --' };
  protected defaultType = '-- Välj typ av larm --';
  protected defaultValj = '-- Välj --';
  protected name?: string;
  protected species = this.defaultSmittamne;
  protected type?: LarmTyp;
  protected status = 'AKTIV';

  protected larmkriteria: any = [];
  protected kriteriaToEdit?: LarmKriteriumDTO;
  protected antibiotikaKriteria: AntibiotikaDTO[] = [];
  protected showSubkriteria = true;
  protected sirValues = ['S', 'I', 'R'];
  protected micValues = MIC_VALUES;

  protected andOrRelations: { [key: number]: string } = {};
  protected isCopy = false;
  protected errorMessage?: string;
  protected undefinedAntibiotics?: string;
  protected SMEs = SMEs;
  constructor(
    private standardNamnService: StandardNamnService,
    private larmKriteriumService: LarmKriteriumService,
    private router: Router,
    private route: ActivatedRoute,
    private destroy: DestroyRef
  ) {
    this.isCopy = this.route.snapshot.queryParamMap.get('copy') === 'true' ? true : false;
    this.kriteriaToEdit = this.router.getCurrentNavigation()?.extras.state as LarmKriteriumDTO;
  }

  ngOnInit(): void {
    this.selectedAnalyser = [];
    this.selectedProvmaterial = [];
    this.standardNamnService.load();
    this.standardNamnService.smittamne.pipe(takeUntilDestroyed(this.destroy)).subscribe((data) => (this.smittamne = data));
    this.standardNamnService.analyser.pipe(takeUntilDestroyed(this.destroy)).subscribe((data) => {
      this.analyser = data;
    });
    this.standardNamnService.provmaterial
      .pipe(takeUntilDestroyed(this.destroy))
      .subscribe((data) => (this.provmaterial = data));
    this.standardNamnService.antibiotika.pipe(takeUntilDestroyed(this.destroy)).subscribe((data) => {
      this.antibiotika = data;
      if (this.kriteriaToEdit) {
        this.mapForEdit(this.kriteriaToEdit);
      }
    });
    this.zoneSubject$.pipe(debounceTime(500), takeUntilDestroyed(this.destroy)).subscribe(zone => {
      this.validateZon(zone);
    })
  }

  private mapForEdit(row: LarmKriteriumDTO) {
    this.name = this.isCopy ? undefined : row.name;
    this.species = this.smittamne.find((amne) => amne.standardNamn === row.species) ?? this.defaultSmittamne;
    this.type = LarmTyp[row.type as keyof typeof LarmTyp];
    this.status = row.status;
    this.selectedAnalyser = this.analyser.filter((analys) => row.analysis.includes(analys.standardNamn));
    this.selectedProvmaterial = this.provmaterial.filter((material) =>
      row.sampleMaterials.includes(material.standardNamn),
    );
    this.analyser = this.analyser.filter((analys) => !row.analysis.includes(analys.standardNamn));
    this.provmaterial = this.provmaterial.filter((material) => !row.sampleMaterials.includes(material.standardNamn));

    this.antibiotikaKriteria = this.mapAntiobiotikaRelationsDbToGui(row.antibiotics);
    this.antibiotikaKriteria.forEach(
      (x) => (x.selectedAntibiotika = this.antibiotika.find((antibiotika) => antibiotika.standardNamn === x.name)),
    );
  }

  protected getKriteriaName() {
    return this.name;
  }

  protected canSave() {
    return (
      (this.kriteriaToEdit || (this.name && this.species !== this.defaultSmittamne)) &&
      this.type !== undefined //&& (this.type !== LarmTyp.LOKALT || this.selectedLab !== undefined)
      &&
      this.checkAntibiotikaRelations() &&
      this.checkAntiobiotikaValid() &&
      this.name !== '' &&
      this.name !== undefined
    );
  }

  private checkAntibiotikaRelations = () => {
    return this.antibiotikaKriteria.length > 0
      ? Object.keys(this.andOrRelations).length === this.antibiotikaKriteria.length - 1
      : true;
  };

  private checkAntiobiotikaValid = () => {
    return this.antibiotikaKriteria.every((x) => {
      return (
        x.selectedAntibiotika !== this.defaultAntibiotika &&
        (x.mic !== this.defaultValj || x.zon !== undefined || x.sir !== this.defaultValj || x.tolk !== this.defaultValj)
      );
    });
  };
  protected save = () => {
    this.errorMessage = undefined;
    this.undefinedAntibiotics = undefined;
    this.antibiotikaKriteria = this.antibiotikaKriteria.filter(
      (x) => x.selectedAntibiotika?.standardNamn !== this.defaultAntibiotika.standardNamn,
    );
    this.antibiotikaKriteria.forEach((x) => (x.name = x.selectedAntibiotika?.standardNamn as string));

    if (this.antibiotikaKriteria.some((kriteria) => kriteria.name === undefined)) {
      this.undefinedAntibiotics = 'Ett fel har inträffat vid sparande. Var god kontrollera namnen på antibiotika.';
      return;
    }

    const kriterium: LarmKriteriumDTO = {
      name: this.name as string,
      period: 1,
      num: 1,
      type:
        this.type === LarmTyp.NATIONELL_INTERN ? 'NATIONELL_INTERN' :'NATIONELL_PUBLIK',
          // ? 'NATIONELL_INTERN':
          // // : this.type === LarmTyp.LOKALT
          // //   ? 'LOKALT'
          //   : 'NATIONELL_PUBLIK',
      status: this.status,
      species: this.species?.standardNamn,
      analysis: this.selectedAnalyser.map((analys) => analys.standardNamn),
      sampleMaterials: this.selectedProvmaterial.map((material) => material.standardNamn),
       labs: [],
        // this.type === LarmTyp.LOKALT && this.selectedLab
        //   ? [this.extractLabValue(this.selectedLab)]
        //   : [],
      antibiotics: this.antibiotikaKriteria.length > 0 ? this.mapAntiobiotikaRelationsGuiToDb() : null,
    };

    if (this.kriteriaToEdit && !this.isCopy) {
      kriterium.id = this.kriteriaToEdit.id;
      this.larmKriteriumService.updateKriterium(kriterium).subscribe((data) => {
        this.router.navigate(['larm-kriterium']);
      });
    } else {
      this.larmKriteriumService.saveKriterium(kriterium).subscribe({
        next: (data) => {
          this.router.navigate(['larm-kriterium']);
        },
        error: (e) => {
          this.errorMessage = e.error;
        },
      });
    }
  };

  private mapAntiobiotikaRelationsGuiToDb = () => {
    const relations: any = { 0: [] };
    const andOrs: any[] = [];

    this.antibiotikaKriteria.forEach((kriteria) => {
      if (typeof kriteria.mic !== 'number') {
        kriteria.mic = undefined;
      }
    });
    relations[0].push(this.antibiotikaKriteria[0]);

    for (let i in this.andOrRelations) {
      andOrs.push(this.andOrRelations[i]);
    }

    let currentIndex = 0;
    andOrs.forEach((relation: string, index: number) => {
      if (relation === 'eller') {
        relations[currentIndex].push(this.antibiotikaKriteria[index + 1]);
      }
      if (relation === 'och') {
        currentIndex++;
        relations[currentIndex] = [];
        relations[currentIndex].push(this.antibiotikaKriteria[index + 1]);
      }
    });

    //Set sortorder for entities within an "eller" group. sortorder for och groups is handle on the backend using the key of the group
    Object.values(relations).forEach((larmdDtoArray: any) => {
      larmdDtoArray.forEach((x: any, index: number) => x.sortOrder = index)
    })
    return relations;
  };

  protected deleteAntibiotikaKriteria = (index: number) => {
    this.andOrRelations = {};
    const newArr = this.antibiotikaKriteria.slice();
    newArr.splice(index, 1);
    this.antibiotikaKriteria = newArr;
  };

  protected updateZone(value: number, kriteria: AntibiotikaDTO){
    this.zoneSubject$.next({value, kriteria});
  }

  protected validateZon({value, kriteria} : {value: number, kriteria: AntibiotikaDTO}) {
    if (value < 6) {
      kriteria.zon = undefined;
      setTimeout(() => {
        kriteria.zon = 6;
      }, 0);
    }

    if (value > 50) {
      kriteria.zon = undefined;
      setTimeout(() => {
        kriteria.zon = 50;
      }, 0);
    }

    if (!isInteger(value)) {
      setTimeout(() => {
        kriteria.zon = Math.trunc(value);
      }, 0);
    }
  }

  protected validateMic(value: any, kriteria: AntibiotikaDTO) {
    if (value < 0) {
      setTimeout(() => {
        kriteria.mic = undefined;
      }, 0);
    }
  }

  private mapAntiobiotikaRelationsDbToGui = (antibiotika: any) => {
    const antibiotikaKriteria: any[] = [];
    let currentIndex = 1;
    for (let [index, value] of Object.entries(antibiotika)) {
      (value as any[]).forEach((x) => antibiotikaKriteria.push(x));
      const length = (value as any[]).length - 1;
      [...Array(length)].forEach((x) => {
        this.andOrRelations[currentIndex] = 'eller';

        currentIndex++;
      });
      if (Number(index) !== Object.entries(antibiotika).length - 1) {
        this.andOrRelations[currentIndex] = 'och';
        currentIndex++;
      }
    }

    return antibiotikaKriteria;
  };

  addAntibiotika() {
    const newAntibiotika = {
      name: '',
      sir: this.defaultValj,
      zon: undefined,
      tolk: this.defaultValj,
      mic: this.defaultValj,
      selectedAntibiotika: this.defaultAntibiotika,
    };
    this.antibiotikaKriteria.push(newAntibiotika);
  }

  protected cancel() { {}
    this.router.navigate(['larm-kriterium']);
  }

  protected onTypeChange(selected: LarmTyp) {
    this.type = selected;

    // if (selected !== LarmTyp.LOKALT) {
    //   this.selectedLab = undefined;
    // }
  }
  private extractLabValue(lab: any): string {
    return lab.name;
  }
}
